import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { tenantAuditLogInfiniteQueryOptions } from "@/features/identity-hub/lib/queries/audit-log.queries";
import { Archive, Check, ClipboardList, Copy, Crown, ExternalLink, FileText, Loader2, MoreHorizontal, Package, Pencil, Plus, RefreshCw, Shield, ShieldPlus, Sparkles, UserPlus, Users, X } from "lucide-react";
import {
  Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { AccessGroupItem, UserAccessEntry, UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { grantableRolesOnOrg, isPerTenantAssignable } from "@/features/identity-hub/lib/access-scope";
import {
  unprojectedWildcardOptions,
  type InviteWildcardTemplate,
} from "@/features/identity-hub/lib/invite-role-options";
import { useGrantablePermissionTupleKeys } from "@/features/identity-hub/hooks/useGrantableCatalogKeys";
import { isSuperTenantMember, orgColor } from "@/features/identity-hub/utils/iam.utils";
import {
  ORG_ACCENT_DRAWER_TOP_CLASS,
  ORG_ACCENT_HEADER_WASH_CLASS,
  orgAccentVar,
} from "@/features/identity-hub/utils/org-accent-surface";
import {
  ORG_SETTINGS_FIELD_TAB,
  orgSettingsChangedTabs,
  type OrgSettingsFieldTab,
} from "@/features/identity-hub/lib/org-settings-dirty";
import { orgSettingsFieldLabel, orgSettingsTabLabel } from "@/features/identity-hub/lib/org-settings-field-labels";
import {
  usePermissions,
  useIsSuperTenant,
  useIamPermissionForTenant,
} from "@/features/auth/hooks/usePermissions";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { industryLabel } from "./OrgCard";
import { OrgLogoMark } from "./OrgLogoMark";
import { ClampedDescription } from "./ClampedDescription";
import { IAM_ORG_DRAWER_MIN_WIDTH_PX, IamOverlayDrawer } from "./shared/drawer/IamOverlayDrawer";
import { OrgDrawerUnsavedChangesGuard, type UnsavedChangeRow } from "./shared/drawer/OrgDrawerUnsavedChangesGuard";
import { DrawerTabStrip, type DrawerTabDef } from "./shared/drawer/DrawerTabStrip";
import { OrgSettingsForm, type OrgSettingsFormApi, type OrgSettingsTab } from "./OrgSettingsModal";
import { OrgSettingsDirtyStatus } from "./OrgSettingsDirtyStatus";
import { OrgKpiAutosaveStatus } from "./OrgKpiAutosaveStatus";
import { OrgDrawerAuditTab } from "./OrgDrawerAuditTab";
import type { AuditTimelineSummary } from "./AuditTimeline";
import { OrgDrawerMembersTab } from "./OrgDrawerMembersTab";
import { OrgDrawerRolesTab } from "./OrgDrawerRolesTab";
import { OrgMembersInviteByEmailPanel } from "./OrgMembersInviteByEmailPanel";
import { OrgDrawerOnboardingTab, type OrgOnboardingFormApi } from "./onboarding/OrgDrawerOnboardingTab";

// ─── Constants ────────────────────────────────────────────────────────────────

const BQ_SYNCED_AT_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
};

const FIELD_TAB_ICONS: Record<OrgSettingsFieldTab, ComponentType<{ className?: string }>> = {
  general: Pencil,
  organization: Crown,
  marketplace: Package,
};

// ─── Types ────────────────────────────────────────────────────────────────────

type DrawerTab = OrgSettingsTab | "onboarding" | "members" | "roles" | "audit";
export type OrgDrawerTab = DrawerTab;

export interface OrgItem {
  id: string;
  name: string;
  type: string;
  productLine?: string;
  createdAt: string;
  logoUrl?: string | null;
  description?: string | null;
  brandColor?: string | null;
  retoolUserId?: string | null;
  /** ISO timestamp set when the org is soft-deleted; `null` for active orgs. */
  deletedAt?: string | null;
  /** Industry indices assigned to this org (from TenantIndustry join table). */
  industryIndices?: string[];
  /** The org's default industry from tenant_settings.defaultIndustry. */
  defaultIndustry?: string | null;
  /** Server aggregate from GET /tenants. Matches the Organizations table. */
  memberCount?: number;
}

export interface OrgDrawerProps {
  org: OrgItem | null;
  members: UserItem[];
  /**
   * Visible membership total for this org. Prefer the tenant-scoped users
   * query totalCount, then `org.memberCount`. The loaded `members` array can
   * be a single page, so the badge must not use `members.length`.
   */
  memberCount?: number;
  membersLoading?: boolean;
  /** First page failed. Distinct from `membersLoading`, which is only the in-flight query. */
  membersError?: unknown;
  onRetryMembers?: () => void;
  hasMoreMembers?: boolean;
  isFetchingMoreMembers?: boolean;
  /** A later page failed. Loaded members stay on screen. */
  membersNextPageFailed?: boolean;
  onLoadMoreMembers?: () => void;
  roles: AccessGroupItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  allUsers: UserItem[];
  isSuperTenant: boolean;
  open?: boolean;
  initialTab?: OrgDrawerTab;
  tabFocusToken?: number;
  onClose: () => void;
  onJumpToPerson: (userId: string) => void;
  onJumpToRole?: (roleId: string) => void;
  onRemoveFromOrg: (userId: string) => void;
  onBulkChangeRole?: (userIds: string[], accessId: string) => void;
  onBulkAddRole?: (userIds: string[], accessId: string) => void;
  onBulkResendInvite?: (inviteIds: string[]) => void;
  onBulkRemoveFromOrg?: (userIds: string[]) => void;
  isBulkMembersBusy?: boolean;
  onRemoveRole?: (roleId: string) => void;
  /** Superadmin cold-assign of an active Wildcard role that is not on this org yet. */
  onApplyWildcardTemplate?: (templateId: string) => void;
  /** Superadmin remove of an unused Wildcard projection. `roleId` is the live access group. */
  onRemoveWildcardTemplate?: (roleId: string) => void;
  /** True while a cold-assign mutation is in flight. */
  isApplyingWildcardRole?: boolean;
  /** True while a cold-remove mutation is in flight. */
  isRemovingWildcardRole?: boolean;
  /** Live access id whose cold-remove is in flight. */
  removingWildcardRoleId?: string | null;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onAddMembers?: (userIds: string[], accessId: string) => void;
  /** Active wildcard catalog roles. Used when the caller is a platform superadmin. */
  wildcardTemplates?: InviteWildcardTemplate[];
  /** Existing accounts picked against a catalog role that is not live on this org yet. */
  onGrantWildcardTemplate?: (
    userIds: string[],
    templateId: string,
  ) => Promise<string | null | void> | string | null | void;
  isAddingMember?: boolean;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  /** Opens the New Role dialog pre-selected to this org. Used by the no-roles empty state. */
  onCreateRole?: (tenantId: string) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
}

// ─── No-roles empty state ─────────────────────────────────────────────────────
//
// Shown in the Members tab (and reachable via the footer CTA) when an org has
// zero roles defined. We can't grant anyone access without an access group, so
// the only meaningful next action is "Create a role" — surface that boldly
// instead of letting the user open an add-member picker that does nothing.

function NoRolesEmptyState({ onCreateRole }: { onCreateRole?: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-4 px-4 py-8 text-center">
      <div className="relative">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 rounded-2xl bg-primary/10 blur-xl"
        />
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/5 shadow-sm">
          <Shield className="h-6 w-6 text-primary" strokeWidth={1.75} />
        </div>
        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-primary/30 bg-card shadow-sm">
          <Sparkles className="h-2.5 w-2.5 text-primary" />
        </span>
      </div>

      <div className="flex max-w-[260px] flex-col gap-1.5">
        <p className="text-sm font-semibold leading-snug text-foreground">
          {t("iam.orgDrawer.noRoles.title", "Set up roles to add members")}
        </p>
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          {t(
            "iam.orgDrawer.noRoles.body",
            "Roles define what people can do in this organization. Create your first role, then invite members and pick which one they get.",
          )}
        </p>
      </div>

      {onCreateRole && (
        <Button size="sm" className="gap-1.5" onClick={onCreateRole}>
          <Plus className="h-3.5 w-3.5" />
          {t("iam.orgDrawer.noRoles.cta", "Create first role")}
        </Button>
      )}

      <ol className="mt-1 flex w-full max-w-[260px] flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3 text-left">
        <li className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold tabular-nums text-primary">
            1
          </span>
          <span>
            {t("iam.orgDrawer.noRoles.step1", "Create a role and pick its permissions.")}
          </span>
        </li>
        <li className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <span className="mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold tabular-nums text-muted-foreground">
            2
          </span>
          <span>
            {t("iam.orgDrawer.noRoles.step2", "Add members and assign them that role.")}
          </span>
        </li>
      </ol>
    </div>
  );
}

// ─── OrgDrawer ────────────────────────────────────────────────────────────────

const SETTINGS_TABS: OrgSettingsTab[] = ["general", "organization", "marketplace", "kpis", "danger"];

function isSettingsTab(tab: DrawerTab): tab is OrgSettingsTab {
  return (SETTINGS_TABS as string[]).includes(tab);
}

function resolveDrawerTab(
  initialTab: OrgDrawerTab,
  canViewOrganizationTab: boolean,
  canViewOnboardingTab: boolean,
): DrawerTab {
  if (initialTab === "organization" && !canViewOrganizationTab) return "general";
  if (initialTab === "onboarding" && !canViewOnboardingTab) return "general";
  return initialTab;
}

export function OrgDrawer({
  org, members, memberCount, membersLoading, membersError, onRetryMembers,
  hasMoreMembers, isFetchingMoreMembers, membersNextPageFailed, onLoadMoreMembers,
  roles, accessByUser, allUsers, isSuperTenant,
  open = true, initialTab = "general", tabFocusToken = 0,
  onClose, onJumpToPerson, onJumpToRole, onRemoveFromOrg,
  onBulkChangeRole, onBulkAddRole, onBulkResendInvite, onBulkRemoveFromOrg, isBulkMembersBusy,
  onEditProfile, onSuspend, onUnsuspend,
  onAddMembers, wildcardTemplates = [], onGrantWildcardTemplate, onApplyWildcardTemplate, onRemoveWildcardTemplate, isApplyingWildcardRole, isRemovingWildcardRole, removingWildcardRoleId, isAddingMember, isRemovingMember, removingMemberId, onCreateRole, onOpenAuditLog,
}: OrgDrawerProps) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const callerIsSuperTenant = useIsSuperTenant();
  const canViewOrganizationTab = callerIsSuperTenant;
  /** Client onboarding follows the normal organization edit permission on this org. */
  const canUpdateOrgOnboarding = useIamPermissionForTenant(org?.id ?? "", "IDENTITY_HUB_UPDATE_TENANT");
  const canViewOnboardingTab = canUpdateOrgOnboarding && !isSuperTenant;
  const grantableKeys = useGrantablePermissionTupleKeys();
  const canViewAuditLog = perms.IDENTITY_HUB_VIEW_AUDIT_LOG.isOn;
  const canInviteByEmail = perms.IDENTITY_HUB_GENERATE_INVITE.isOn || callerIsSuperTenant;
  const resolvedInitialTab = resolveDrawerTab(initialTab, canViewOrganizationTab, canViewOnboardingTab);
  const [tab, setTab] = useState<DrawerTab>(resolvedInitialTab);
  const [isAddPeopleOpen, setIsAddPeopleOpen] = useState(false);
  const [selectedMemberCount, setSelectedMemberCount] = useState(0);
  const [auditSummary, setAuditSummary] = useState<AuditTimelineSummary>({ shown: 0, hasMore: false });
  const [saveApi, setSaveApi] = useState<OrgSettingsFormApi | null>(null);
  const [onboardingApi, setOnboardingApi] = useState<OrgOnboardingFormApi | null>(null);
  // Mounted on first visit and kept, so the BigQuery read runs only when asked
  // for and an unsaved draft survives switching tabs.
  const [hasVisitedOnboarding, setHasVisitedOnboarding] = useState(false);
  const [settingsFormKey, setSettingsFormKey] = useState(0);
  const [unsavedCloseGuardOpen, setUnsavedCloseGuardOpen] = useState(false);

  const isArchived = !!org?.deletedAt;
  const displayedMemberCount = memberCount ?? org?.memberCount ?? members.length;

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);

  const eligibleUsers = useMemo(
    () => allUsers.filter((u) => !memberIds.has(u.id)),
    [allUsers, memberIds],
  );

  const assignableRoles = useMemo(
    () =>
      grantableRolesOnOrg(roles, grantableKeys, {
        includeFixedCatalog: callerIsSuperTenant,
      }),
    [roles, grantableKeys, callerIsSuperTenant],
  );

  const unprojectedWildcards = useMemo(
    () => (callerIsSuperTenant ? unprojectedWildcardOptions(roles, wildcardTemplates) : []),
    [callerIsSuperTenant, roles, wildcardTemplates],
  );
  // Same population the email picker can select. FIXED stays grantable on the
  // members tab but is not an invite target, so it must not keep this count up.
  const inviteRoleCount =
    assignableRoles.filter((role) => isPerTenantAssignable(role)).length + unprojectedWildcards.length;
  const hasInviteRole = inviteRoleCount > 0;

  const hasAdgeStaffMember = useMemo(
    () => members.some((m) => isSuperTenantMember(accessByUser.get(m.id))),
    [members, accessByUser],
  );

  const canAddPeople = eligibleUsers.length > 0 || canInviteByEmail;
  const isShowingAddPeople = isAddPeopleOpen && tab === "members";

  useEffect(() => {
    if (!org?.id) return;
    setTab(resolveDrawerTab(initialTab, canViewOrganizationTab, canViewOnboardingTab));
    setIsAddPeopleOpen(false);
    setUnsavedCloseGuardOpen(false);
  }, [org?.id, initialTab, tabFocusToken, canViewOrganizationTab, canViewOnboardingTab]);

  useEffect(() => {
    setHasVisitedOnboarding(false);
    setOnboardingApi(null);
  }, [org?.id]);

  useEffect(() => {
    if (tab === "onboarding") setHasVisitedOnboarding(true);
  }, [tab]);

  // Audit count for the tab badge — same infinite query the Audit tab uses,
  // so React Query shares the cache and only the first page is fetched here.
  // NOTE: this hook must run on every render (incl. when `org` is null) — early-returning
  // before it would change the hook count and crash with "Rendered fewer hooks than expected".
  const { data: auditData } = useInfiniteQuery({
    ...tenantAuditLogInfiniteQueryOptions(org?.id ?? "", { pageSize: 20 }),
    enabled: canViewAuditLog && !!org,
  });

  const auditCount = auditData?.pages[0]?.items.length ?? undefined;
  const settingsTab: OrgSettingsTab = isSettingsTab(tab) ? tab : "general";
  const settingsDirty =
    !!saveApi?.isDirty && !isSuperTenant && !isArchived && !saveApi.isSaving;
  const onboardingDirty = !!onboardingApi?.isDirty && !onboardingApi.isSaving;
  // Org KPIs persist per edit; the form Save only matters there when another tab has pending edits.
  const isKpiAutosaveFooter = tab === "kpis" && !settingsDirty;

  const handleClose = () => {
    setUnsavedCloseGuardOpen(false);
    onClose();
  };

  const requestClose = () => {
    if (settingsDirty || onboardingDirty) {
      setUnsavedCloseGuardOpen(true);
      return;
    }
    handleClose();
  };

  const handleTabChange = (next: DrawerTab) => {
    setTab(next);
  };

  const handleDrawerOpenChange = (next: boolean) => {
    if (!next) requestClose();
  };

  // Remounting the form reseeds every field from the saved org and cached admin settings.
  const handleDiscardSettings = () => setSettingsFormKey((key) => key + 1);

  const handleSaveAndClose = async () => {
    const isSettingsSaved = settingsDirty && saveApi ? await saveApi.handleSave() : true;
    const isOnboardingSaved = isSettingsSaved && onboardingDirty && onboardingApi ? await onboardingApi.handleSave() : true;
    if (isSettingsSaved && isOnboardingSaved) handleClose();
    else setUnsavedCloseGuardOpen(false);
  };

  const changedFields = settingsDirty ? saveApi.changedFields : [];
  const changedTabs = orgSettingsChangedTabs(changedFields);
  const unsavedChangeCount = changedFields.length + (onboardingDirty ? 1 : 0);
  const unsavedTabCount = changedTabs.length + (onboardingDirty ? 1 : 0);
  const isSaveAndCloseDisabled =
    (settingsDirty && (!saveApi?.canSave || saveApi.isSaving)) ||
    (onboardingDirty && (!onboardingApi?.canSave || onboardingApi.isSaving));
  const onboardingChangeRows: UnsavedChangeRow[] = onboardingDirty
    ? [{
        id: "onboarding",
        title: t("iam.orgDrawer.tabs.onboarding", "Client onboarding"),
        detail: t("iam.onboarding.unsavedDetail", "Unsaved client_context changes"),
        Icon: ClipboardList,
        onReview: () => {
          setTab("onboarding");
          setUnsavedCloseGuardOpen(false);
        },
      }]
    : [];
  const settingsChangeRows: UnsavedChangeRow[] = changedFields.map((field) => {
    const fieldTab = ORG_SETTINGS_FIELD_TAB[field];
    return {
      id: field,
      title: orgSettingsFieldLabel(t, field),
      detail: orgSettingsTabLabel(t, fieldTab),
      Icon: FIELD_TAB_ICONS[fieldTab],
      onReview: () => {
        setTab(fieldTab);
        setUnsavedCloseGuardOpen(false);
      },
    };
  });
  const unsavedChangeRows = [...settingsChangeRows, ...onboardingChangeRows];

  const bqSyncedLabel = saveApi?.lastBqSyncedAt
    ? t("iam.orgDrawer.header.bqSynced", "BQ synced {{date}}", {
        date: new Date(saveApi.lastBqSyncedAt).toLocaleString(undefined, BQ_SYNCED_AT_FORMAT),
      })
    : null;

  const tabs: Array<DrawerTabDef<DrawerTab>> = [
    {
      id: "general",
      label: t("iam.orgDrawer.tabs.general", "General"),
      Icon: Pencil,
      testId: "org-settings-tab-general",
      hasUnsavedChanges: changedTabs.includes("general"),
    },
    ...(canViewOrganizationTab ? [{
      id: "organization" as const,
      label: t("iam.orgDrawer.tabs.organization", "Organization"),
      Icon: Crown,
      testId: "org-settings-tab-organization",
      hasUnsavedChanges: changedTabs.includes("organization"),
    }] : []),
    ...(canViewOnboardingTab ? [{
      id: "onboarding" as const,
      label: t("iam.orgDrawer.tabs.onboarding", "Client onboarding"),
      Icon: ClipboardList,
      testId: "org-drawer-tab-onboarding",
      hasUnsavedChanges: onboardingDirty,
    }] : []),
    { id: "members", label: t("iam.orgDrawer.tabs.members", "Members"), Icon: Users, count: displayedMemberCount },
    { id: "roles", label: t("iam.orgDrawer.tabs.roles", "Roles"), Icon: Shield, count: roles.length },
    {
      id: "marketplace",
      label: t("iam.orgDrawer.tabs.orders", "Orders"),
      Icon: Package,
      testId: "org-settings-tab-marketplace",
      hasUnsavedChanges: changedTabs.includes("marketplace"),
    },
    { id: "kpis", label: t("iam.orgDrawer.tabs.kpis", "Org KPIs"), Icon: Sparkles, testId: "org-settings-tab-kpis" },
    { id: "audit", label: t("iam.orgDrawer.tabs.audit", "Audit"), Icon: FileText, count: auditCount },
    ...(!isSuperTenant ? [{
      id: "danger" as const,
      label: t("iam.orgDrawer.tabs.danger", "Danger zone"),
      Icon: Archive,
      tone: "danger" as const,
      testId: "org-settings-tab-danger",
    }] : []),
  ];

  return (
    <IamOverlayDrawer
      open={open && !!org}
      onOpenChange={handleDrawerOpenChange}
      title={org?.name ?? t("iam.dashboard.tabs.orgs")}
      minWidthPx={IAM_ORG_DRAWER_MIN_WIDTH_PX}
    >
      {org ? (
        <div
          className={cn(
            "relative flex min-h-0 flex-1 flex-col overflow-hidden bg-card",
            !isSuperTenant && ORG_ACCENT_DRAWER_TOP_CLASS,
          )}
          style={!isSuperTenant ? orgAccentVar(org.brandColor ?? orgColor(org.name)) : undefined}
        >
        {/* ── Head ─────────────────────────────────────────── */}
        <div
          className={cn(
            "flex items-start justify-between gap-2.5 border-b border-border/40 px-5 py-4",
            !isSuperTenant && ORG_ACCENT_HEADER_WASH_CLASS,
          )}
          style={!isSuperTenant ? orgAccentVar(org.brandColor ?? orgColor(org.name)) : undefined}
        >
          <div className="flex min-w-0 items-start gap-3">
            <ImagePreviewTrigger src={org.logoUrl ?? null} alt={org.name}>
              <OrgLogoMark
                name={org.name}
                logoUrl={org.logoUrl ?? null}
                accent={org.brandColor ?? orgColor(org.name)}
                isSuperTenant={isSuperTenant}
                size={44}
              />
            </ImagePreviewTrigger>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-[15px] font-semibold leading-tight">{org.name}</h3>
                {isSuperTenant && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600"
                          aria-label={t("iam.orgCard.superTenant.iconLabel", "Platform organization")}
                        >
                          <Crown className="h-2.5 w-2.5" />
                          {t("iam.orgDrawer.platformBadge", "Adge")}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t("iam.orgCard.superTenant.tooltip", "The platform operator tenant — used for cross-tenant administration and settings managed by Adge.")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {isArchived ? (
                  <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    <Archive className="h-2.5 w-2.5" />
                    {t("iam.orgDrawer.archivedBadge", "Archived")}
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-success-subtle px-1.5 py-[3px] text-[10px] font-medium leading-none text-success-subtle-foreground">
                    {t("iam.orgDrawer.activeBadge", "Active")}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {[
                  industryLabel(org.type),
                  org.productLine === "CORE"
                    ? t("iam.orgDrawer.settings.productLineCore", "Core")
                    : org.productLine === "COMP"
                      ? t("iam.orgDrawer.settings.productLineComp", "Comp")
                      : org.productLine === "CORE_AND_COMP"
                        ? t("iam.orgDrawer.settings.productLineCoreAndComp", "Core & Comp")
                        : null,
                  bqSyncedLabel,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {org.description && (
                <ClampedDescription text={org.description} maxLines={3} className="mt-1" />
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {!isSuperTenant && saveApi && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        data-testid="org-drawer-sync-bq"
                        disabled={!saveApi.canSyncWithBq}
                        aria-label={t("iam.orgDrawer.header.syncWithBq", "Sync with BQ")}
                        onClick={saveApi.handleSyncWithBq}
                      >
                        {saveApi.isSyncingBq || saveApi.isSavingPartitionForSync ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        <span className="hidden sm:inline">
                          {saveApi.isSavingPartitionForSync
                            ? t("iam.orgDrawer.settings.savingPartition", "Saving partition…")
                            : saveApi.isSyncingBq
                              ? t("iam.orgDrawer.settings.syncing", "Syncing…")
                              : t("iam.orgDrawer.settings.syncWithBq", "Sync with BQ")}
                        </span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {saveApi.syncPartitionEmpty && (
                    <TooltipContent side="bottom">
                      {t("iam.orgDrawer.settings.setPartitionFirst", "Set a Customer Partition first")}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t("iam.personDrawer.actions.more", "More actions")}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => navigator.clipboard.writeText(org.id)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t("iam.orgDrawer.copyId", "Copy ID")}
                </DropdownMenuItem>
                {onOpenAuditLog && canViewAuditLog && (
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() => onOpenAuditLog({ searchPrefill: org.name })}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t("iam.orgDrawer.viewAuditLog", "View audit log")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={requestClose}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("iam.orgDrawer.closePanel", "Close panel")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <DrawerTabStrip tabs={tabs} activeTab={tab} onTabChange={handleTabChange} />

        {/* ── Body ─────────────────────────────────────────── */}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            isSettingsTab(tab) || tab === "onboarding" ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          <div
            data-testid="org-settings-form-slot"
            className={cn("min-h-0 flex-1 flex-col", isSettingsTab(tab) ? "flex" : "hidden")}
            inert={!isSettingsTab(tab) || undefined}
            aria-hidden={!isSettingsTab(tab)}
          >
            <OrgSettingsForm
              key={settingsFormKey}
              org={org}
              isSuperTenant={isSuperTenant}
              variant="embedded"
              tab={settingsTab}
              onClose={handleClose}
              onFormApiChange={setSaveApi}
              members={members}
            />
          </div>
          {canViewOnboardingTab && hasVisitedOnboarding && (
            <div
              className={cn("min-h-0 flex-1 flex-col", tab === "onboarding" ? "flex" : "hidden")}
              inert={tab !== "onboarding" || undefined}
              aria-hidden={tab !== "onboarding"}
            >
              <OrgDrawerOnboardingTab
                tenantId={org.id}
                locked={isArchived}
                onFormApiChange={setOnboardingApi}
              />
            </div>
          )}
          {tab === "members" && (
            <OrgDrawerMembersTab
              orgId={org.id}
              orgName={org.name}
              members={members}
              memberTotal={displayedMemberCount}
              membersLoading={membersLoading}
              membersError={membersError}
              onRetryMembers={onRetryMembers}
              hasMoreMembers={hasMoreMembers}
              isFetchingMoreMembers={isFetchingMoreMembers}
              membersNextPageFailed={membersNextPageFailed}
              onLoadMoreMembers={onLoadMoreMembers}
              accessByUser={accessByUser}
              assignableRoles={assignableRoles}
              roles={roles}
              canManageMembers={!isSuperTenant && !isArchived}
              hasRoles={roles.length > 0}
              onJumpToPerson={onJumpToPerson}
              onRemoveFromOrg={onRemoveFromOrg}
              onBulkChangeRole={onBulkChangeRole}
              onBulkAddRole={onBulkAddRole}
              onBulkResendInvite={onBulkResendInvite}
              onBulkRemoveFromOrg={onBulkRemoveFromOrg}
              onSelectedCountChange={setSelectedMemberCount}
              isRemovingMember={isRemovingMember}
              removingMemberId={removingMemberId}
              isBulkBusy={isBulkMembersBusy}
              onEditProfile={onEditProfile}
              onSuspend={onSuspend}
              onUnsuspend={onUnsuspend}
              onOpenAuditLog={onOpenAuditLog && canViewAuditLog ? (user) => onOpenAuditLog({ searchPrefill: user.email }) : undefined}
              noRolesEmpty={
                <NoRolesEmptyState
                  onCreateRole={onCreateRole && !isSuperTenant && !isArchived ? () => onCreateRole(org.id) : undefined}
                />
              }
            />
          )}

          {tab === "roles" && (
            <OrgDrawerRolesTab
              orgName={org.name}
              roles={roles}
              memberCountByRole={new Map(roles.map((role) => [role.id, role.memberCount ?? 0]))}
              onJumpToRole={onJumpToRole}
              addRoleOptions={callerIsSuperTenant ? unprojectedWildcardOptions(roles, wildcardTemplates) : []}
              onAddWildcardRole={callerIsSuperTenant ? onApplyWildcardTemplate : undefined}
              addPending={isApplyingWildcardRole}
              onRemoveWildcardRole={callerIsSuperTenant ? onRemoveWildcardTemplate : undefined}
              removePending={isRemovingWildcardRole}
              removingRoleId={removingWildcardRoleId}
              noRolesEmpty={
                <NoRolesEmptyState
                  onCreateRole={onCreateRole && !isSuperTenant && !isArchived ? () => onCreateRole(org.id) : undefined}
                />
              }
            />
          )}

          {tab === "audit" && (
            <OrgDrawerAuditTab
              tenantId={org.id}
              hasPermission={canViewAuditLog}
              onEntryClick={onOpenAuditLog ? (id) => onOpenAuditLog({ highlightId: id }) : undefined}
              onSummaryChange={setAuditSummary}
            />
          )}
        </div>

        {/* ── Add people panel ─────────────────────────────── */}
        {isShowingAddPeople && (
          <OrgMembersInviteByEmailPanel
            orgId={org.id}
            orgName={org.name}
            assignableRoles={assignableRoles}
            wildcardTemplates={callerIsSuperTenant ? wildcardTemplates : []}
            candidates={eligibleUsers}
            members={members}
            canInviteByEmail={canInviteByEmail}
            onAddMembers={(userIds, accessId) => onAddMembers?.(userIds, accessId)}
            onGrantTemplate={(userIds, templateId) => onGrantWildcardTemplate?.(userIds, templateId)}
            onClose={() => setIsAddPeopleOpen(false)}
            onComplete={() => setIsAddPeopleOpen(false)}
          />
        )}

        {/* ── Footer ───────────────────────────────────────── */}
        {!isShowingAddPeople && tab !== "onboarding" && (
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          {isKpiAutosaveFooter && !isSuperTenant && !isArchived && (
            <OrgKpiAutosaveStatus tenantId={org.id} />
          )}
          {isSettingsTab(tab) && !isKpiAutosaveFooter && saveApi && !saveApi.isDirty && !saveApi.isSaving && !isSuperTenant && !isArchived && (
            <span className="mr-auto flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="org-settings-all-saved">
              <Check className="size-3.5 text-success-subtle-foreground" aria-hidden />
              {t("iam.orgDrawer.footer.allSaved", "All changes saved")}
            </span>
          )}
          {isSettingsTab(tab) && settingsDirty && (
            saveApi.saveHint && !saveApi.canSave ? (
              <span className="mr-auto text-[11px] text-muted-foreground">{saveApi.saveHint}</span>
            ) : (
              <OrgSettingsDirtyStatus changedFields={saveApi.changedFields} />
            )
          )}
          {isSettingsTab(tab) && settingsDirty && (
            <Button
              size="sm"
              variant="ghost"
              data-testid="org-settings-discard"
              disabled={saveApi.isSyncingBq}
              onClick={handleDiscardSettings}
            >
              {t("iam.orgDrawer.settings.discard", "Discard")}
            </Button>
          )}
          {isSettingsTab(tab) && !isKpiAutosaveFooter && (
            <Button
              size="sm"
              className="gap-1.5"
              data-testid="org-settings-save"
              disabled={isSuperTenant || isArchived || !saveApi?.isDirty || !saveApi.canSave}
              onClick={() => void saveApi?.handleSave()}
            >
              {saveApi?.isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {saveApi?.isSaving ? t("iam.orgDrawer.settings.saving", "Saving…") : t("iam.orgDrawer.settings.saveChanges", "Save changes")}
            </Button>
          )}
          {tab === "members" && (roles.length === 0 && onCreateRole && !isSuperTenant && !isArchived ? (
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => onCreateRole(org.id)}
            >
              <ShieldPlus className="h-3.5 w-3.5" />
              {t("iam.orgDrawer.noRoles.cta", "Create first role")}
            </Button>
          ) : tab === "members" ? (
            <>
              <span className="mr-auto text-xs text-muted-foreground" data-testid="org-drawer-members-summary">
                {selectedMemberCount > 0
                  ? [
                      t("iam.orgDrawer.membersFooter.selected", "{{selected}} of {{total}} selected", {
                        selected: selectedMemberCount,
                        total: displayedMemberCount,
                      }),
                      hasAdgeStaffMember
                        ? t("iam.orgDrawer.membersFooter.adgeExcluded", "Adge staff are excluded from bulk actions")
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : [
                      t("iam.orgDrawer.membersFooter.memberCount", {
                        count: displayedMemberCount,
                        defaultValue_one: "{{count}} member",
                        defaultValue_other: "{{count}} members",
                      }),
                      t("iam.orgDrawer.membersFooter.grantableRoles", {
                        count: inviteRoleCount,
                        defaultValue_one: "{{count}} role you can grant",
                        defaultValue_other: "{{count}} roles you can grant",
                      }),
                    ].join(" · ")}
              </span>
              <Button
                size="sm"
                className="gap-1.5"
                data-testid="org-drawer-add-member-cta"
                disabled={isSuperTenant || isArchived || !hasInviteRole || !canAddPeople}
                title={
                  !hasInviteRole
                    ? t("iam.orgDrawer.addMember.noGrantableRoles", "No roles you can grant")
                    : undefined
                }
                onClick={() => setIsAddPeopleOpen(true)}
              >
                {isAddingMember ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5" />
                )}
                {t("iam.orgDrawer.addMember.cta", "Add member")}
              </Button>
            </>
          ) : null)}
          {tab === "audit" && canViewAuditLog && (
            <>
              {auditSummary.shown > 0 && (
                <span className="mr-auto text-xs text-muted-foreground" data-testid="org-drawer-audit-summary">
                  {[
                    t("iam.orgDrawer.auditFooter.showingCount", {
                      count: auditSummary.shown,
                      defaultValue_one: "Showing {{count}} event",
                      defaultValue_other: "Showing {{count}} events",
                    }),
                    auditSummary.hasMore
                      ? t("iam.orgDrawer.auditFooter.moreAvailable", "more available")
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              )}
              {onOpenAuditLog && (
                <Button
                  size="sm"
                  variant="outline"
                  iconEnd="external-link"
                  data-testid="org-drawer-open-audit-log"
                  onClick={() => onOpenAuditLog({ searchPrefill: org.name })}
                >
                  {t("iam.orgDrawer.auditFooter.openFullLog", "Open full audit log")}
                </Button>
              )}
            </>
          )}
        </div>
        )}
          <OrgDrawerUnsavedChangesGuard
            open={unsavedCloseGuardOpen}
            title={t("iam.orgDrawer.unsavedGuard.titleNamed", "Save changes to {{name}}?", { name: org.name })}
            body={t("iam.orgDrawer.unsavedGuard.bodyCounts", {
              count: unsavedChangeCount,
              tabs: t("iam.orgDrawer.unsavedGuard.tabCount", {
                count: unsavedTabCount,
                defaultValue_one: "{{count}} tab",
                defaultValue_other: "{{count}} tabs",
              }),
              defaultValue_one: "You have {{count}} unsaved change on {{tabs}}. Closing now throws it away.",
              defaultValue_other: "You have {{count}} unsaved changes on {{tabs}}. Closing now throws them away.",
            })}
            discardLabel={t("iam.orgDrawer.unsavedGuard.discardCount", {
              count: unsavedChangeCount,
              defaultValue_one: "Discard {{count}} change",
              defaultValue_other: "Discard {{count}} changes",
            })}
            changes={unsavedChangeRows}
            onKeepEditing={() => setUnsavedCloseGuardOpen(false)}
            onDiscard={handleClose}
            onSaveAndClose={() => void handleSaveAndClose()}
            isSaveAndCloseDisabled={isSaveAndCloseDisabled}
          />
        </div>
      ) : null}
    </IamOverlayDrawer>
  );
}
